import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface Payment {
  id: string;
  playerId: string;
  teamId: string;
  amount: number;
  amountPaid: number;
  dueDate?: string;
  status: 'paid' | 'partial' | 'unpaid';
  description?: string;
  paymentMethod?: string;
  paidAt?: string;
  notes?: string;
  createdAt: string;
  player?: { id: string; firstName: string; lastName: string; email: string };
  team?: { id: string; name: string };
}

const Payments: React.FC = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await api.get('/payments/my-payments');
      setPayments(response.data.payments || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async (paymentId: string) => {
    try {
      const response = await api.post(`/payments/${paymentId}/checkout`);
      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to start checkout');
    }
  };

  const filteredPayments = payments.filter((p) => {
    if (filter === 'all') return true;
    return p.status === filter;
  });

  const totalOwed = payments
    .filter((p) => p.status !== 'paid')
    .reduce((sum, p) => sum + (p.amount - p.amountPaid), 0);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>My Payments</h1>
        {totalOwed > 0 && (
          <div style={{ background: '#FFF3E0', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold' }}>
            Total Outstanding: ${totalOwed.toFixed(2)}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
        >
          <option value="all">All</option>
          <option value="unpaid">Unpaid</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {filteredPayments.length === 0 ? (
        <p style={{ color: '#757575' }}>No payments found.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Team</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Paid</th>
              <th>Due Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.map((payment) => (
              <tr key={payment.id}>
                <td>{payment.team?.name || 'N/A'}</td>
                <td>{payment.description || '-'}</td>
                <td>${payment.amount.toFixed(2)}</td>
                <td>${payment.amountPaid.toFixed(2)}</td>
                <td>{payment.dueDate || '-'}</td>
                <td>
                  <span
                    className={`badge ${
                      payment.status === 'paid'
                        ? 'badge-success'
                        : payment.status === 'partial'
                        ? 'badge-warning'
                        : 'badge-danger'
                    }`}
                  >
                    {payment.status}
                  </span>
                </td>
                <td>
                  {payment.status !== 'paid' && (
                    <button className="btn btn-primary btn-sm" onClick={() => handlePayNow(payment.id)}>
                      Pay Now
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Payments;
